import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ResolvedBlogPost } from '../../types/blog';
import { formatPostDate } from '../../lib/blog';
import { useLocalizedPath } from '../../i18n/useLocalizedPath';

interface PostHeaderProps {
  post: ResolvedBlogPost;
}

export function PostHeader({ post }: PostHeaderProps) {
  const { t } = useTranslation('blog');
  const path = useLocalizedPath();
  const category = t(`category.${post.category}`, post.category);

  return (
    <header className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10">
      <nav className="text-xs text-gray-500 mb-4" aria-label="breadcrumb">
        <Link to={path('home')} className="hover:text-primary-600 transition-colors">
          {t('breadcrumb.home', 'Início')}
        </Link>
        <span className="mx-2">/</span>
        <Link to={path('blog')} className="hover:text-primary-600 transition-colors">
          {t('breadcrumb.blog', 'Blog')}
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-700">{category}</span>
      </nav>

      <span className="inline-block text-[11px] font-semibold text-primary-600 uppercase tracking-widest">
        {category}
      </span>

      <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight tracking-tight">
        {post.title}
      </h1>

      <p className="mt-4 text-base sm:text-lg text-gray-600 leading-relaxed">
        {post.excerpt}
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
        <span>
          {t('card.by', 'Por')}{' '}
          <Link
            to={`${path('sobre')}#autor`}
            className="font-semibold text-gray-800 hover:text-primary-600 transition-colors"
          >
            {post.author}
          </Link>
        </span>
        <span aria-hidden="true">·</span>
        <time dateTime={post.publishedAt}>
          {formatPostDate(post.publishedAt, post.locale)}
        </time>
        <span aria-hidden="true">·</span>
        <span>{t('card.read_time', '{{min}} min de leitura', { min: post.readTimeMinutes })}</span>
      </div>

      <figure className="mt-8 -mx-4 sm:mx-0 sm:rounded-2xl overflow-hidden bg-gray-100">
        <img
          src={post.coverImage}
          alt={post.coverImageAlt}
          width={975}
          height={576}
          className="w-full h-auto"
        />
      </figure>
    </header>
  );
}

export default PostHeader;
